import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Skeleton,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useNavigate, useParams } from 'react-router-dom';
import { getMatter } from '../../actions/matters/getMatter';
import { createRafExtract } from '../../actions/matters/createRafExtract';
import { updateMatter } from '../../actions/matters/updateMatter';
import { getMatterSettings } from '../../actions/matters/rafSettings';
import UploadLoaderModal from '../../components/modals/UploadLoaderModal';
import { uploadFilesToS3 } from '../../utils/fileUploadHelper';
import { getCurrentUserId } from '../../utils/getCurrentUserId';
import type { Matter, MatterStatus } from '../../types/matter';
import RafFieldsForm from './RafFieldsForm';
import SourceDocumentsPanel from './SourceDocumentsPanel';
import TasksTab from './tabs/TasksTab';
import NotesTab from './tabs/NotesTab';
import DocumentsTab from './tabs/DocumentsTab';
import AssessmentsTab from './tabs/AssessmentsTab';
import PrescriptionBadge from '../../components/matters/PrescriptionBadge';
import PrescriptionOverrideModal from '../../components/matters/PrescriptionOverrideModal';
import EditMatterReferenceModal from '../../components/matters/EditMatterReferenceModal';
import SectionCompletionPanel from '../../components/matters/SectionCompletionPanel';
import PdfPasswordModal from '../../components/modals/PdfPasswordModal';
import { usePdfPasswordHandler } from '../../hooks/usePdfPasswordHandler';
import { reKeyPasswordsByS3Key } from '../../utils/reKeyPasswordsByS3Key';
import { getClaimantName } from '../../utils/matter-display';

const STATUS_COLORS: Record<MatterStatus, string> = {
  draft: 'var(--t3)',
  active: 'var(--ok)',
  urgent: 'var(--warn)',
  complete: 'var(--teal)',
  archived: 'var(--t4)',
};

const STATUS_LABELS: Record<MatterStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  urgent: 'Urgent',
  complete: 'Complete',
  archived: 'Archived',
};

type DetailTab = 'tasks' | 'notes' | 'documents' | 'assessments' | 'review';

const ACCEPTED_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
const ACCEPTED_EXT = '.pdf,.docx,.jpg,.jpeg,.png';
const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 30;

const MatterDetailView: React.FC = () => {
  const { matterId } = useParams<{ matterId: string }>();
  const navigate = useNavigate();

  const [matter, setMatter] = useState<Matter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Upload state
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [intelligentMode, setIntelligentMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Password-protected PDF handling — reuses RAF's existing pdf.js client-side
  // detector + modal (usePdfPasswordHandler). filePasswordsRef mirrors the
  // filename-keyed map so handleModalUpload can re-key it to S3 keys at dispatch.
  const pdfPw = usePdfPasswordHandler();
  const filePasswordsRef = useRef<Record<string, string>>({});
  useEffect(() => {
    filePasswordsRef.current = pdfPw.filePasswords;
  }, [pdfPw.filePasswords]);

  // Processing modal state
  const [userSub, setUserSub] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFiles, setModalFiles] = useState<File[]>([]);
  const [extractJobId, setExtractJobId] = useState<string>('');

  const [activeTab, setActiveTab] = useState<DetailTab>('tasks');
  const [prescriptionOverrideOpen, setPrescriptionOverrideOpen] = useState(false);
  const [userRefModalOpen, setUserRefModalOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const fetchMatter = useCallback(async () => {
    if (!matterId) return;
    setLoading(true);
    setError('');
    try {
      const result = await getMatter(matterId);
      setMatter(result);
    } catch {
      setError('Failed to load matter.');
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchMatter();
  }, [fetchMatter]);

  useEffect(() => {
    let cancelled = false;
    getMatterSettings()
      .then((s) => {
        if (!cancelled) setIntelligentMode(Boolean(s.intelligentModeDefault));
      })
      .catch(() => {
        // leave default off
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCurrentUserId()
      .then((sub) => {
        if (!cancelled) setUserSub(sub);
      })
      .catch(() => {
        // Modal will fall back to polling if sub is unavailable.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter((f) => ACCEPTED_TYPES.includes(f.type));
    addFiles(dropped);
  }, []);

  const addFiles = (newFiles: File[]) => {
    setFileError('');
    const oversized = newFiles.find((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (oversized) {
      setFileError(`${oversized.name} exceeds ${MAX_FILE_SIZE_MB} MB limit.`);
      return;
    }
    setFiles((prev) => {
      const combined = [...prev, ...newFiles].slice(0, MAX_FILES);
      if (prev.length + newFiles.length > MAX_FILES) {
        setFileError(`Maximum ${MAX_FILES} files allowed.`);
      }
      return combined;
    });
    // Detect password-protected PDFs (client-side pdf.js) and prompt for the
    // password; the collected passwords are re-keyed to S3 keys at upload time.
    void pdfPw.handleFilesAdded(newFiles);
  };

  const removeFile = (index: number) => {
    const removed = files[index];
    setFiles((prev) => prev.filter((_, i) => i !== index));
    if (removed) pdfPw.removeFile(removed);
    setFileError('');
  };

  const handleExtract = () => {
    if (files.length === 0 || !matterId) return;
    setFileError('');
    setExtractJobId('');
    setModalFiles(files);
    setModalOpen(true);
  };

  const handleModalUpload = useCallback(
    async (
      filesToUpload: File[],
      progressCallback: (progress: number) => void,
    ): Promise<string> => {
      if (!matterId) throw new Error('Matter not loaded.');
      const uploaded = await uploadFilesToS3(filesToUpload, progressCallback);
      const fileKeys = uploaded.map((u) => u.key);
      // Re-key the collected passwords (filename-keyed) to S3 keys so the
      // backend can look them up as filePasswords[s3Key] (shared util —
      // same transform useUploadForm/useDualUploadForm use).
      const s3KeyPasswords = reKeyPasswordsByS3Key(uploaded, filePasswordsRef.current);
      const jobId = await createRafExtract(matterId, fileKeys, intelligentMode, {
        filePasswords: Object.keys(s3KeyPasswords).length > 0 ? s3KeyPasswords : undefined,
      });
      setExtractJobId(jobId);
      setFiles([]);
      pdfPw.clearFiles();
      return 'RAF field extraction started';
    },
    // pdfPw.clearFiles is a stable useCallback ref; filePasswordsRef is a ref —
    // neither changes identity per render, so handleModalUpload stays stable.
    [matterId, intelligentMode, pdfPw.clearFiles],
  );

  const handleModalComplete = useCallback(() => {
    fetchMatter();
  }, [fetchMatter]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setModalFiles([]);
    setExtractJobId('');
    fetchMatter();
  }, [fetchMatter]);

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 3, md: 4 }, pb: 8 }}>
        <Skeleton variant="text" width={260} height={36} sx={{ bgcolor: 'var(--s2)' }} />
        <Skeleton variant="rounded" height={220} sx={{ bgcolor: 'var(--s2)', mt: 2.5, borderRadius: '12px' }} />
      </Box>
    );
  }

  if (error || !matter) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 3, md: 4 }, pb: 8 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/matters')} sx={{ color: 'var(--t2)', textTransform: 'none', mb: 2, fontWeight: 500 }}>
          Back to Matters
        </Button>
        <Typography sx={{ color: 'var(--err)', fontSize: '0.9rem' }}>{error || 'Matter not found.'}</Typography>
        <Button size="small" onClick={fetchMatter} sx={{ mt: 1, color: 'var(--teal)', textTransform: 'none', fontWeight: 600 }}>Retry</Button>
      </Box>
    );
  }

  const hasExtractedData = !!matter.rafExtractedJson && Object.keys(matter.rafExtractedJson).length > 0;
  const claimantName = getClaimantName(matter);
  const createdLabel = new Date(matter.createdAt).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 3, md: 4 }, pb: 8, maxWidth: 1320, mx: 'auto' }}>
      {/* Header — Direction A "Brief" language: clean type, mono ref pill, grouped actions */}
      <Box sx={{ mb: 3.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
          <IconButton
            onClick={() => navigate('/matters')}
            size="small"
            sx={{
              color: 'var(--t3)',
              p: 0.5,
              borderRadius: '8px',
              '&:hover': { bgcolor: 'var(--s2)', color: 'var(--t1)' },
            }}
            aria-label="Back to matters"
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            onClick={() => navigate('/matters')}
            sx={{
              color: 'var(--t3)',
              fontSize: '0.78rem',
              cursor: 'pointer',
              '&:hover': { color: 'var(--t1)' },
            }}
          >
            Matters
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {claimantName && (
              <Typography
                data-testid="matter-claimant-name"
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.45rem' },
                  fontWeight: 600,
                  color: 'var(--t1)',
                  letterSpacing: '-0.015em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: 220, md: 360 },
                }}
              >
                {claimantName}
              </Typography>
            )}
            <Box
              sx={{
                px: 0.75,
                py: 0.25,
                bgcolor: 'var(--s2)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.74rem',
                color: 'var(--t2)',
                fontWeight: 500,
              }}
            >
              {matter.ref}
            </Box>
            {matter.userRef ? (
              <Box
                onClick={() => setUserRefModalOpen(true)}
                data-testid="matter-user-ref"
                role="button"
                aria-label="Edit your reference"
                sx={{
                  px: 0.75,
                  py: 0.25,
                  bgcolor: 'color-mix(in srgb, var(--teal) 8%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--teal) 18%, transparent)',
                  borderRadius: '6px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.74rem',
                  color: 'var(--teal)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'color-mix(in srgb, var(--teal) 14%, transparent)' },
                }}
              >
                {matter.userRef}
              </Box>
            ) : null}
            {/* Always render the edit affordance — pencil when no userRef, secondary edit when one exists. */}
            <IconButton
              size="small"
              onClick={() => setUserRefModalOpen(true)}
              aria-label={matter.userRef ? 'Edit your reference' : 'Add your reference'}
              data-testid="edit-user-ref-btn"
              sx={{ color: 'var(--t3)', '&:hover': { color: 'var(--teal)', bgcolor: 'var(--s2)' }, p: 0.5, borderRadius: '6px' }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <StatusChip status={matter.status} />
            {matter.prescription && Object.keys(matter.prescription).length > 0 && (
              <PrescriptionBadge
                prescription={matter.prescription as {
                  date?: string;
                  daysRemaining?: number | null;
                  explanation?: string;
                  isHitAndRun?: boolean;
                  isMinor?: boolean;
                }}
                size="medium"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {hasExtractedData && (
              <GhostButton onClick={() => setPrescriptionOverrideOpen(true)}>
                Override Prescription
              </GhostButton>
            )}
            <GhostButton
              disabled={statusSaving}
              onClick={async () => {
                if (!matter) return;
                const nextStatus: MatterStatus = matter.status === 'archived' ? 'active' : 'archived';
                setStatusSaving(true);
                try {
                  const updated = await updateMatter(matter.matterId, { status: nextStatus });
                  setMatter(updated);
                } catch {
                  setError('Failed to update matter status.');
                } finally {
                  setStatusSaving(false);
                }
              }}
            >
              {matter.status === 'archived' ? 'Unarchive' : 'Archive'}
            </GhostButton>
          </Box>
        </Box>
        <Typography sx={{ color: 'var(--t3)', fontSize: '0.76rem', mt: 0.75 }}>
          Created {createdLabel}
        </Typography>
      </Box>

      {/* Upload + Extract section (pre-extraction) */}
      {!hasExtractedData && (
        <Box
          sx={{
            bgcolor: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            p: 3,
            mb: 3,
          }}
        >
          <Typography sx={{ color: 'var(--t1)', fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>
            Upload Documents
          </Typography>
          <Typography sx={{ color: 'var(--t3)', fontSize: '0.82rem', mb: 2 }}>
            Upload consultation notes, police reports, medical records, or any RAF claim documentation. The AI will extract ~195 fields from the uploaded documents.
          </Typography>

          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '1.5px dashed var(--border)',
              borderRadius: '10px',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': { borderColor: 'var(--teal)', bgcolor: 'var(--s1)' },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 36, color: 'var(--t3)', mb: 1 }} />
            <Typography sx={{ color: 'var(--t1)', fontSize: '0.86rem', fontWeight: 500 }}>
              Drag &amp; drop files here, or click to browse
            </Typography>
            <Typography sx={{ color: 'var(--t4)', fontSize: '0.74rem', mt: 0.5 }}>
              PDF, DOCX, JPG, PNG — up to {MAX_FILE_SIZE_MB} MB each, max {MAX_FILES} files
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={ACCEPTED_EXT}
              onChange={(e) => {
                if (e.target.files) addFiles(Array.from(e.target.files));
                e.target.value = '';
              }}
            />
          </Box>

          {files.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {files.map((f, i) => (
                <Box
                  key={`${f.name}-${i}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: 'var(--s1)',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                  }}
                >
                  <InsertDriveFileIcon sx={{ fontSize: 18, color: 'var(--t3)' }} />
                  <Typography sx={{ flex: 1, fontSize: '0.82rem', color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'var(--t4)', flexShrink: 0, fontFamily: 'ui-monospace, monospace' }}>
                    {(f.size / 1024).toFixed(0)} KB
                  </Typography>
                  <IconButton size="small" onClick={() => removeFile(i)} sx={{ color: 'var(--t4)', '&:hover': { color: 'var(--err)' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {fileError && (
            <Typography sx={{ color: 'var(--err)', fontSize: '0.8rem', mt: 1 }}>{fileError}</Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
            <FormControlLabel
              control={<Switch checked={intelligentMode} onChange={(e) => setIntelligentMode(e.target.checked)} size="small" />}
              label={
                <Typography sx={{ fontSize: '0.82rem', color: 'var(--t2)' }}>
                  Intelligent Mode
                </Typography>
              }
            />
            <Button
              variant="contained"
              disabled={files.length === 0 || modalOpen}
              onClick={handleExtract}
              startIcon={modalOpen ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
              sx={{
                bgcolor: 'var(--teal)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.84rem',
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                py: 0.85,
                boxShadow: 'none',
                '&:hover': { bgcolor: 'var(--teal-mid, #2c3578)', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: 'var(--s2)', color: 'var(--t4)' },
              }}
            >
              {modalOpen ? 'Extracting...' : 'Extract Fields'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Tabbed detail view (post-extraction) */}
      {hasExtractedData && matter.rafExtractedJson && (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '280px 1fr' } }}>
          <Box sx={{ order: { xs: 2, md: 0 } }}>
            <Box sx={{ position: 'sticky', top: 24, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SourceDocumentsPanel
                matterId={matter.matterId}
                sourceFiles={matter.sourceFiles}
                onExtractionComplete={fetchMatter}
              />
              <SectionCompletionPanel
                extracted={matter.rafExtractedJson}
                naSections={matter.naSections}
              />
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {/* Underline tab nav — Stripe/Linear pattern. Defeats the global
                MuiTab pill theme (scribeTheme.ts) so the active tab reads as a
                clean underlined label instead of a filled blue pill (which made
                dark text invisible on dark blue background). */}
            <Box sx={{ borderBottom: '1px solid var(--border)', mb: 2.5 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v as DetailTab)}
                TabIndicatorProps={{
                  sx: {
                    bgcolor: 'var(--teal) !important',
                    height: '2px !important',
                    borderRadius: '2px 2px 0 0 !important',
                    display: 'block !important',
                  },
                }}
                sx={{
                  minHeight: 38,
                  // Defeat global MuiTabs pill container (rounded grey wrapper).
                  bgcolor: 'transparent !important',
                  border: 'none !important',
                  borderRadius: '0 !important',
                  padding: '0 !important',
                  '& .MuiTabs-flexContainer': { gap: '8px' },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '0.84rem',
                    fontWeight: 500,
                    color: 'var(--t3)',
                    minHeight: 38,
                    py: 0.75,
                    px: 1.5,
                    minWidth: 'auto',
                    flex: '0 0 auto',
                    borderRadius: '0 !important',
                    '&:hover': {
                      color: 'var(--t1)',
                      backgroundColor: 'transparent !important',
                    },
                  },
                  // Defeat global Mui-selected pill (background + boxShadow + white text).
                  '& .MuiTab-root.Mui-selected': {
                    color: 'var(--t1) !important',
                    background: 'transparent !important',
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important',
                    fontWeight: 600,
                  },
                }}
              >
                <Tab value="tasks" label="Tasks" />
                <Tab value="notes" label="Notes" />
                <Tab value="documents" label="Documents" />
                <Tab value="assessments" label="Assessments" />
                <Tab value="review" label="Review & Edit" />
              </Tabs>
            </Box>
            {activeTab === 'tasks' && <TasksTab matterId={matter.matterId} />}
            {activeTab === 'notes' && <NotesTab matterId={matter.matterId} />}
            {activeTab === 'documents' && (
              <DocumentsTab
                matterId={matter.matterId}
                docsGenerated={matter.docsGenerated}
                onRefresh={fetchMatter}
              />
            )}
            {activeTab === 'assessments' && (
              <AssessmentsTab
                matterId={matter.matterId}
                meritsAssessment={matter.meritsAssessment}
                quantumAssessment={matter.quantumAssessment}
                userSub={userSub}
                onRefresh={fetchMatter}
              />
            )}
            {activeTab === 'review' && (
              <RafFieldsForm
                key={matter.updatedAt}
                matterId={matter.matterId}
                extractedData={matter.rafExtractedJson}
                naSections={matter.naSections}
                onSaved={fetchMatter}
              />
            )}
          </Box>
        </Box>
      )}

      <PrescriptionOverrideModal
        open={prescriptionOverrideOpen}
        onClose={() => setPrescriptionOverrideOpen(false)}
        matterId={matter.matterId}
        currentDate={
          matter.prescription && typeof (matter.prescription as Record<string, unknown>)['date'] === 'string'
            ? ((matter.prescription as Record<string, unknown>)['date'] as string)
            : undefined
        }
        onSuccess={fetchMatter}
      />

      <EditMatterReferenceModal
        open={userRefModalOpen}
        onClose={() => setUserRefModalOpen(false)}
        matterId={matter.matterId}
        currentUserRef={matter.userRef}
        onSuccess={(updated) => setMatter(updated)}
      />

      <UploadLoaderModal
        open={modalOpen}
        files={modalFiles}
        jobId={extractJobId}
        userId={userSub ?? undefined}
        onUpload={handleModalUpload}
        onComplete={handleModalComplete}
        onClose={handleModalClose}
      />

      <PdfPasswordModal
        open={pdfPw.showPasswordModal}
        file={pdfPw.currentFileNeedingPassword}
        onSubmit={pdfPw.handlePasswordSubmit}
        onSkip={pdfPw.handlePasswordSkip}
        onCancel={pdfPw.handlePasswordCancel}
      />
    </Box>
  );
};

const StatusChip: React.FC<{ status: MatterStatus }> = ({ status }) => {
  const color = STATUS_COLORS[status];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        px: 0.875,
        py: 0.3125,
        bgcolor: `color-mix(in srgb, ${color} 12%, transparent)`,
        borderRadius: '6px',
        fontSize: '0.72rem',
        fontWeight: 600,
        color,
        letterSpacing: '0.01em',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
      {STATUS_LABELS[status]}
    </Box>
  );
};

const GhostButton: React.FC<React.PropsWithChildren<{ onClick: () => void; disabled?: boolean }>> = ({ onClick, disabled, children }) => (
  <Button
    size="small"
    variant="outlined"
    disabled={disabled}
    onClick={onClick}
    sx={{
      textTransform: 'none',
      borderColor: 'var(--border)',
      color: 'var(--t2)',
      fontSize: '0.78rem',
      fontWeight: 500,
      borderRadius: '8px',
      px: 1.5,
      py: 0.5,
      bgcolor: 'var(--bg)',
      '&:hover': { borderColor: 'var(--t2)', color: 'var(--t1)', bgcolor: 'var(--s1)' },
      '&.Mui-disabled': { borderColor: 'var(--border)', color: 'var(--t4)' },
    }}
  >
    {children}
  </Button>
);

export default MatterDetailView;
